import { useState, useRef } from "react";
import { applyTheme } from "../../constants/themes";
import { ACCENT_LOCALES, getAvailableVoices, speak } from "../../utils/words";
import { IconPicker } from "../shared/IconPicker";
import { ThemePicker } from "../shared/ThemePicker";
import { setUserLayoutPreference, fbSet } from "../../utils/firebase";

import maisha1 from "../../assets/avatars/maisha-1.webp";
import maisha2 from "../../assets/avatars/maisha-2.webp";
import maisha3 from "../../assets/avatars/maisha-3.webp";
import maisha4 from "../../assets/avatars/maisha-4.webp";
import aariv1 from "../../assets/avatars/aariv-1.webp";
import aariv2 from "../../assets/avatars/aariv-2.webp";
import aariv3 from "../../assets/avatars/aariv-3.webp";
import aariv4 from "../../assets/avatars/aariv-4.webp";

const AVATAR_IMAGES = {
  "maisha-1": maisha1, "maisha-2": maisha2, "maisha-3": maisha3, "maisha-4": maisha4,
  "aariv-1":  aariv1,  "aariv-2":  aariv2,  "aariv-3":  aariv3,  "aariv-4":  aariv4,
};

const MAISHA_AVATARS = ["maisha-1", "maisha-2", "maisha-3", "maisha-4"];
const AARIV_AVATARS  = ["aariv-1",  "aariv-2",  "aariv-3",  "aariv-4"];

export function PlannerProfile({ user, data, onDataChange, onClose }) {
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [newPin, setNewPin]                 = useState("");
  const [confirmPin, setConfirmPin]         = useState("");
  const [pwMsg, setPwMsg]                   = useState(null);
  const [saved, setSaved]                   = useState("");
  const fileRef = useRef();

  const userData = data.users.find(u => u.id === user.id);

  function toast() { setSaved("Saved ✓"); setTimeout(() => setSaved(""), 1500); }

  function updateUser(changes) {
    const newData = { ...data, users: data.users.map(u => u.id === user.id ? { ...u, ...changes } : u) };
    onDataChange(newData);
    toast();
  }

  function handleIconSelect(ic) { updateUser({ avatar: ic }); setShowIconPicker(false); }

  function handleThemeSelect(t) { updateUser({ theme: t }); applyTheme(t); }

  function handleBgUpload(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => updateUser({ bgImage: e.target.result });
    reader.readAsDataURL(file);
  }

  function handlePinChange() {
    if (!/^\d{4}$/.test(newPin) || newPin !== confirmPin) { setPwMsg({ ok: false, text: "PINs must be 4 digits and match." }); return; }
    updateUser({ pin: newPin });
    setNewPin(""); setConfirmPin("");
    setPwMsg({ ok: true, text: "PIN updated!" });
    setTimeout(() => setPwMsg(null), 3000);
  }

  function handleLayoutSelect(layout) {
    updateUser({ layoutPreference: layout });
    setUserLayoutPreference(user.id, layout);
  }

  function handleAvatarSelect(slug) {
    updateUser({ avatarImage: slug });
    fbSet(`users/${user.id}/avatarImage`, slug);
  }

  // Derived values
  const currentLayout = userData?.layoutPreference || "modern";
  const childName     = userData?.name || "";
  const childAvatars  = childName === "Maisha" ? MAISHA_AVATARS : childName === "Aariv" ? AARIV_AVATARS : null;
  const defaultAvatar = childName === "Maisha" ? "maisha-1" : "aariv-4";
  const currentAvatar = userData?.avatarImage || defaultAvatar;

  return (
    <div className="index-card rounded-xl p-6 max-w-xl">
      <div className="flex justify-between items-center mb-5">
        <div className="font-handwritten text-3xl text-stone-800">My Profile — {userData?.name}</div>
        <div className="flex gap-2 items-center">
          {saved && <span className="text-sm text-green-600 font-medium">{saved}</span>}
          {onClose && <button className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-200 border border-stone-200" onClick={onClose}>✕ Close</button>}
        </div>
      </div>

      {/* ── LAYOUT STYLE — children only ──────────────────────────────────────── */}
      {user.role === "child" && (
        <div className="bg-stone-50 rounded-xl p-4 mb-4">
          <div className="font-semibold text-sm text-stone-700 mb-3">Layout Style</div>
          <div className="flex gap-3">
            {[
              { id: "modern",    icon: "📋", label: "Modern",    sub: "Clean dashboard" },
              { id: "adventure", icon: "🗺️", label: "Adventure", sub: "Gamified map"    },
            ].map(opt => {
              const active = currentLayout === opt.id;
              return (
                <div key={opt.id} onClick={() => handleLayoutSelect(opt.id)}
                  className={`rounded-xl p-4 border-2 cursor-pointer text-center transition-all flex-1 ${
                    active
                      ? "border-amber-400 bg-amber-50"
                      : "border-stone-200 bg-white hover:bg-stone-50"
                  }`}>
                  {active && (
                    <span className="absolute top-1 right-2 bg-amber-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5">✓</span>
                  )}
                  <div className="text-2xl mb-1">{opt.icon}</div>
                  <div className="font-bold text-sm">{opt.label}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{opt.sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── YOUR AVATAR — children only ───────────────────────────────────────── */}
      {user.role === "child" && childAvatars && (
        <div className="bg-stone-50 rounded-xl p-4 mb-4">
          <div className="font-semibold text-sm text-stone-700 mb-3">Your Avatar</div>
          <div className="flex gap-3 flex-wrap">
            {childAvatars.map(slug => {
              const active = currentAvatar === slug;
              return (
                <img key={slug} src={AVATAR_IMAGES[slug]} alt={slug}
                  onClick={() => handleAvatarSelect(slug)}
                  className={`w-16 h-16 rounded-full object-cover cursor-pointer transition-all ${
                    active
                      ? "border-2 border-amber-400 shadow-md ring-2 ring-amber-200"
                      : "border-2 border-stone-200"
                  }`}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* ── THEME / SKIN ──────────────────────────────────────────────────────── */}
      <div className="bg-stone-50 rounded-xl p-4 mb-4">
        <div className="font-semibold text-sm text-stone-700 mb-3">Theme / Skin</div>
        <ThemePicker current={userData?.theme || "ocean"} onSelect={handleThemeSelect} />
      </div>

      {/* ── PROFILE ICON ──────────────────────────────────────────────────────── */}
      <div className="bg-stone-50 rounded-xl p-4 mb-4">
        <div className="font-semibold text-sm text-stone-700 mb-3">Profile Icon</div>
        <div className="flex items-center gap-3">
          <span className="text-4xl">{userData?.avatar}</span>
          <button className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-200 border border-stone-200" onClick={() => setShowIconPicker(s => !s)}>
            {showIconPicker ? "Close" : "Change Icon"}
          </button>
        </div>
        {showIconPicker && <IconPicker current={userData?.avatar} onSelect={handleIconSelect} />}
      </div>

      {/* ── BACKGROUND IMAGE ──────────────────────────────────────────────────── */}
      <div className="bg-stone-50 rounded-xl p-4 mb-4">
        <div className="font-semibold text-sm text-stone-700 mb-3">Background Image</div>
        <div className="text-xs text-stone-600 mb-3">Upload a photo — it will appear behind all your screens</div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => handleBgUpload(e.target.files[0])} />
        {userData?.bgImage ? (
          <div className="flex gap-3 items-center">
            <img src={userData.bgImage} alt="bg" className="w-20 h-12 object-cover rounded-lg border-2 border-stone-300" />
            <button className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-200 border border-stone-200" onClick={() => fileRef.current.click()}>Change</button>
            <button className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600" onClick={() => updateUser({ bgImage: null })}>Remove</button>
          </div>
        ) : (
          <div className="border-2 border-dashed border-stone-300 rounded-xl p-6 text-center cursor-pointer transition-all hover:border-amber-400 hover:bg-amber-50 text-stone-500 font-medium text-sm" onClick={() => fileRef.current.click()}
            onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("border-amber-400", "bg-amber-50"); }}
            onDragLeave={e => e.currentTarget.classList.remove("border-amber-400", "bg-amber-50")}
            onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove("border-amber-400", "bg-amber-50"); handleBgUpload(e.dataTransfer.files[0]); }}>
            📷 Tap to choose a photo, or drag and drop here
          </div>
        )}
      </div>

      {/* ── CHANGE PIN ────────────────────────────────────────────────────────── */}
      <div className="bg-stone-50 rounded-xl p-4 mb-4">
        <div className="font-semibold text-sm text-stone-700 mb-3">🔐 Change PIN</div>
        <div className="flex gap-3 mb-3">
          <input className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" type="password" placeholder="New PIN (4 digits)" maxLength={4} value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, ""))} />
          <input className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" type="password" placeholder="Confirm PIN" maxLength={4} value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ""))} />
        </div>
        <button className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600" onClick={handlePinChange}>Update PIN</button>
        {pwMsg && <div className={`text-left mt-2 text-sm ${pwMsg.ok ? "text-green-600" : "text-red-600"}`}>{pwMsg.text}</div>}
      </div>

      {/* ── VOICE PREFERENCES — children only ────────────────────────────────── */}
      {user.role === "child" && (
        <div className="bg-stone-50 rounded-xl p-4 mb-4">
          <div className="font-semibold text-sm text-stone-700 mb-3">🔊 Spelling Test Voice</div>
          <div className="text-xs text-stone-600 mb-3">Customise how words are read aloud during spelling tests. Available voices depend on your device.</div>

          <div className="mb-4">
            <div className="text-xs font-bold text-stone-700 mb-2">Accent</div>
            <div className="flex gap-2 flex-wrap">
              {ACCENT_LOCALES.map(a => {
                const avail    = getAvailableVoices().some(v => v.lang.startsWith(a.code.split("-")[0]));
                const selected = (userData?.voicePrefs?.accent || "en-AU") === a.code;
                return (
                  <button key={a.code} type="button"
                    onClick={() => updateUser({ voicePrefs: { ...(userData?.voicePrefs || {}), accent: a.code } })}
                    className={`border-2 rounded-full px-3 py-1.5 text-sm font-bold transition-all cursor-pointer ${
                      selected
                        ? "border-amber-500 bg-amber-500 text-white"
                        : "border-stone-300 bg-white text-stone-700 hover:border-amber-300"
                    } ${!avail ? "opacity-50" : ""}`}
                    style={{ opacity: avail ? 1 : 0.5 }}>
                    {a.label}{!avail && " ✗"}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-4">
            <div className="text-xs font-bold text-stone-700 mb-2">Voice Gender</div>
            <div className="flex gap-2">
              {[["any","🎲 Auto"],["female","👩 Female"],["male","👨 Male"]].map(([val, label]) => {
                const selected = (userData?.voicePrefs?.gender || "any") === val;
                return (
                  <button key={val} type="button"
                    onClick={() => updateUser({ voicePrefs: { ...(userData?.voicePrefs || {}), gender: val } })}
                    className={`border-2 rounded-full px-3 py-1.5 text-sm font-bold transition-all cursor-pointer ${
                      selected
                        ? "border-amber-500 bg-amber-500 text-white"
                        : "border-stone-300 bg-white text-stone-700 hover:border-amber-300"
                    }`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <div className="flex-1">
              <div className="font-bold text-sm text-stone-700">💬 Sentence Mode</div>
              <div className="text-xs text-stone-600 mt-1">Show a "Hear it in a sentence" button during spelling tests</div>
            </div>
            <button type="button"
              onClick={() => updateUser({ voicePrefs: { ...(userData?.voicePrefs || {}), sentenceMode: !(userData?.voicePrefs?.sentenceMode ?? true) } })}
              className={`border-2 rounded-full px-3 py-1.5 text-sm font-bold transition-all cursor-pointer min-w-14 ${
                (userData?.voicePrefs?.sentenceMode ?? true)
                  ? "border-amber-500 bg-amber-500 text-white"
                  : "border-stone-300 bg-white text-stone-700 hover:border-amber-300"
              }`}>
              {(userData?.voicePrefs?.sentenceMode ?? true) ? "On" : "Off"}
            </button>
          </div>

          <button className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-200 border border-stone-200 w-full mb-2" onClick={() => speak("The quick brown fox jumps over the lazy dog", userData?.voicePrefs)}>
            🔊 Test Voice
          </button>
          <div className="text-xs text-stone-600">
            Selected: {ACCENT_LOCALES.find(a => a.code === (userData?.voicePrefs?.accent || "en-AU"))?.label || "Australian"} · {userData?.voicePrefs?.gender === "male" ? "Male" : userData?.voicePrefs?.gender === "female" ? "Female" : "Auto"}
          </div>
        </div>
      )}
    </div>
  );
}
